import React from "react";
import { DropdownInputPropsType, InputCostumeErrorType } from "types/form-types";
import { removeAt } from "utilities/app-utilities";
import CoreComponent from "utilities/component-utilities/CoreComponent";
import { validateInput } from "utilities/input-utilities";
import CoreDropdownInput, { CoreDropdownInputClass } from "./dropdown/CoreDropdownInput";

export type MultiSelectInputPropsType<T, TFilterType = any, TSortType = any> = Omit<DropdownInputPropsType<T, TFilterType, TSortType>, 'value' | 'onChange' | 'onClear' | 'onBlur' | 'customErrors'> & {
    value?: T[],
    onChange?: (s: T[] | undefined, event: React.MouseEvent | React.KeyboardEvent | React.FocusEvent) => void,
    min?: number,
    max?: number,
    checkIfItemExists?: (source: T, destination: T) => boolean,
    customErrors?: InputCostumeErrorType<T[], HTMLInputElement>[]
}

class MultiSelectInput<T, TFilterType = any, TSortType = any> extends CoreComponent<MultiSelectInputPropsType<T, TFilterType, TSortType>, any> {
    private readonly coreDropdownInputRef = React.createRef<CoreDropdownInputClass<T, TFilterType, TSortType>>();
    constructor(props: MultiSelectInputPropsType<T, TFilterType, TSortType>) {
        super(props);
        this.coreDropdownInputRef = React.createRef<CoreDropdownInputClass<T, TFilterType, TSortType>>();
    }

    public getInputValue = () => {
        return this.coreDropdownInputRef.current?.getInputValue();
    }
    
    private onAdd = (i: T | undefined, event: React.MouseEvent | React.KeyboardEvent | React.FocusEvent) => {
        if (i) {
            if(this.props.checkIfItemExists) {
                if(this.props.value?.some(c => this.props.checkIfItemExists!(i, c))) return;
            } else if (this.props.value?.some(c => this.props.getItemId(c) === this.props.getItemId(i))) return;
            this.setData(i, event);
        }
    }

    private setData = (data: T | T[], event: React.MouseEvent | React.KeyboardEvent | React.FocusEvent) => {
        if (!this.props.onChange) return;
        if (Array.isArray(data)) this.props.onChange(data, event);
        else
            this.props.onChange([
                ...(this.props.value || []),
                data
            ], event);
    }

    private onItemClick = (_: T, index: number, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.props.value) return;
        this.setData(removeAt(this.props.value, index), e);
    }

    private getPrefixes = () => {
        if (!this.props.value) return <></>;
        return <ul className="flex flex-wrap flex-row max-w-full gap-1">
            {this.props.value!.map((i, index) => {
                return (
                    <li key={`multiselect-${this.props.name}-${this.props.getItemId(i)}`}
                        onClick={e => this.onItemClick(i, index, e)}
                        className="cursor-pointer hover:bg-danger bg-primary text-white px-2 py-1"
                    >
                        {this.props.view.inputView(i)}
                    </li>
                )
            })}
        </ul>
    }

    private validate = (soft: boolean, input: HTMLInputElement | undefined, inputWrapper: HTMLDivElement | undefined) => {
        const customErrors = [...(this.props.customErrors || [])];
        if (this.props.required) {
            customErrors.push({
                name: 'valueMissing',
                message: 'form.errors.valueMissing',
                validate: () => {
                    return this.props.value?.length ? true : false;
                },
            })
        }

        if (this.props.max) {
            customErrors.push({
                name: 'valueMissing',
                message: 'form.errors.valueMissing',
                validate: () => {
                    return this.props.value && this.props.value?.length <= this.props.max! ? true : false;
                },
            })
        }


        if (this.props.min) {
            customErrors.push({
                name: 'valueMissing',
                message: 'form.errors.valueMissing',
                validate: () => {
                    return !this.props.value?.length || this.props.value?.length >= this.props.min! ? true : false;
                },
            })
        }

        const errors = validateInput({
            value: this.props.value,
            input,
            inputWrapper,
            customErrors: customErrors,
            soft: soft ? true : false
        });

        return errors || [];
    }

    render(): React.ReactNode {
        return (
            <CoreDropdownInput
                {...this.props}
                dropdownRef={this.coreDropdownInputRef}
                validate={this.validate}
                value={undefined}
                onChange={this.onAdd}
                onBlur={() => { }}
                prefixes={this.getPrefixes()}
                required={false}
                customErrors={[]}
            />
        );
    }
};

export default MultiSelectInput;
